import { Move } from '../types/index.js';

export class History {
  private past: Move[] = [];
  private future: Move[] = [];
  private maxSize: number;

  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize;
  }

  /**
   * Add a move to the history
   * Clears the redo stack
   */
  push(move: Move): void {
    this.past.push(move);
    this.future = []; // Clear redo stack

    // Limit history size to prevent memory issues
    if (this.past.length > this.maxSize) {
      this.past.shift();
    }
  }

  /**
   * Undo the last move
   * Returns the move to undo, or null if no moves to undo
   */
  undo(): Move | null {
    const move = this.past.pop();
    if (move) {
      this.future.push(move);
      return move;
    }
    return null;
  }

  /**
   * Redo the last undone move
   * Returns the move to redo, or null if no moves to redo
   */
  redo(): Move | null {
    const move = this.future.pop();
    if (move) {
      this.past.push(move);
      return move;
    }
    return null;
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.past.length > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.future.length > 0;
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.past = [];
    this.future = [];
  }

  /**
   * Get the number of moves in history
   */
  getMoveCount(): number {
    return this.past.length;
  }

  /**
   * Get all past moves (for debugging/statistics)
   */
  getPastMoves(): Move[] {
    return [...this.past];
  }

  /**
   * Restore history from saved moves (for deserialization)
   */
  restore(moves: Move[]): void {
    this.past = [...moves];
    this.future = [];
  }
}
